import React, { useState, useEffect } from 'react';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { statusOptions } from '../../utils';

import EditableField from './EditableField';
import MonthSelector from './MonthSelector';
import MultilineEditableField from './MultilineEditableField';
import DistributionSelector from './DistributionSelector';
import StatusSelector from './StatusSelector';
import CompletionPercentage from './CompletionPercentage';
import FinancialSummary from './FinancialSummary';
import ContractsList from './ContractsList';
import PaymentsList from './PaymentsList';
import ActivityFeed from './ActivityFeed';

// Format currency values
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function ScopeDetail({ scope: propScope, scopeName, onBack }) {
  // scopeName is actually shortName
  const shortName = scopeName;
  const [scope, setScope] = useState(propScope);
  const [loading, setLoading] = useState(!propScope && !!shortName);
  const [error, setError] = useState(null);
  
  // Function to refresh scope data
  const refreshScopeData = async () => {
    if (!shortName) return;
    
    try {
      // Save current scroll position to maintain user's place after refresh
      const scrollPosition = window.scrollY;
      
      // Import and call the API
      const { getScopeByShortName } = await import('../../utils/api');
      const updatedScope = await getScopeByShortName(shortName);
      
      if (updatedScope) {
        setScope(updatedScope);
        
        // Restore scroll position after update
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
        }, 0);
      }
    } catch (err) {
      console.error('Failed to refresh scope data:', err);
    }
  };
  
  // Fetch scope data if shortName is provided but no scope
  useEffect(() => {
    const fetchScopeData = async () => {
      if (shortName && !propScope) {
        try {
          setLoading(true);
          setError(null);
          const { getScopeByShortName } = await import('../../utils/api');
          const scopeData = await getScopeByShortName(shortName);
          
          if (scopeData) {
            setScope(scopeData);
          } else {
            setError(`Scope with short_name "${shortName}" not found`);
          }
        } catch (err) {
          setError(`Error loading scope: ${err.message}`);
          console.error('Failed to load scope:', err);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchScopeData();
  }, [shortName, propScope]);

  // Find initial status option matching the scope's status or default to first option
  const initialStatus = scope ? 
    statusOptions.find(option => option.name === scope.status) || statusOptions[0] : 
    statusOptions[0];
  
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusError, setStatusError] = useState(null);
  
  // For completion percentage
  const [completionPercent, setCompletionPercent] = useState(scope ? scope.percentage_complete || 0 : 0);
  const [updatingCompletion, setUpdatingCompletion] = useState(false);
  const [completionError, setCompletionError] = useState(null);
  
  // Update status and completion when scope changes
  useEffect(() => {
    if (scope) {
      const statusOption = statusOptions.find(option => option.name === scope.status) || statusOptions[0];
      setSelectedStatus(statusOption);
      setCompletionPercent(scope.percentage_complete || 0);
    }
  }, [scope]);
  
  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (!scope) return;
    
    // First update the UI
    setSelectedStatus(newStatus);
    setUpdatingStatus(true);
    setStatusError(null);
    
    try {
      // Import is inside to avoid circular dependencies
      const { updateScopeStatus } = await import('../../utils/api');
      
      // Call the API to update the status
      const result = await updateScopeStatus(scope.short_name, newStatus.name);
      
      if (result.success) {
        // Update was successful
        console.log(result.message);
        // Refresh scope data to update the Activity feed
        refreshScopeData();
      } else {
        // Update failed
        setStatusError('Failed to update status on the server');
        // Revert to previous status
        setSelectedStatus(initialStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setStatusError(`Error: ${error.message}`);
      // Revert to previous status
      setSelectedStatus(initialStatus);
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // Function to round percentage to nearest 5%
  const roundToNearestFive = (value) => {
    return Math.round(value / 5) * 5;
  };
  
  // Handle completion percentage change
  const handleCompletionChange = async (changeAmount) => {
    if (!scope) return;
    
    // Calculate new percentage, ensure it's between 0-100, and round to nearest 5%
    const newPercentage = Math.min(100, Math.max(0, roundToNearestFive(completionPercent + changeAmount)));
    
    // If no actual change, don't do anything
    if (newPercentage === completionPercent) return;
    
    // Update UI first
    setCompletionPercent(newPercentage);
    setUpdatingCompletion(true);
    setCompletionError(null);
    
    try {
      // Import is inside to avoid circular dependencies
      const { updateScopeCompletion } = await import('../../utils/api');
      
      // Call the API to update the completion percentage
      await updateScopeCompletion(scope.short_name, newPercentage);
      
      // Refresh scope data to update the Activity feed
      refreshScopeData();
      
      // If completion is set to 100%, also update status to "Complete"
      if (newPercentage === 100 && selectedStatus.name !== 'Complete') {
        const completeStatus = statusOptions.find(option => option.name === 'Complete');
        if (completeStatus) {
          setSelectedStatus(completeStatus);
          const { updateScopeStatus } = await import('../../utils/api');
          await updateScopeStatus(scope.short_name, 'Complete');
          // Refresh scope data to update the Activity feed
          refreshScopeData();
        }
      }
      
      // If completion is set to 0%, also update status to "Not Started On site"
      if (newPercentage === 0 && selectedStatus.name !== 'Not Started On site') {
        const notStartedStatus = statusOptions.find(option => option.name === 'Not Started On site');
        if (notStartedStatus) {
          setSelectedStatus(notStartedStatus);
          const { updateScopeStatus } = await import('../../utils/api');
          await updateScopeStatus(scope.short_name, 'Not Started On site');
          // Refresh scope data to update the Activity feed
          refreshScopeData();
        }
      }
      
    } catch (error) {
      console.error('Error updating completion percentage:', error);
      setCompletionError(`Error: ${error.message}`);
      // Revert to previous percentage
      setCompletionPercent(scope.percentage_complete || 0);
    } finally {
      setUpdatingCompletion(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="px-4 py-6 text-center">
        <div className="animate-pulse">
          <div className="h-5 w-40 bg-gray-300 rounded mx-auto"></div>
          <div className="h-4 w-64 bg-gray-200 rounded mx-auto mt-3"></div>
          <div className="mt-6 h-96 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="px-4 py-6">
        <button
          onClick={onBack}
          className="inline-flex items-center mb-6 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowUturnLeftIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
          Back
        </button>
        
        <div className="text-center">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }
  
  // No scope selected state
  if (!scope) {
    return (
      <div className="px-4 py-6">
        <button
          onClick={onBack}
          className="inline-flex items-center mb-6 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowUturnLeftIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
          Back
        </button>
        
        <div className="text-center">
          <p>No scope selected</p>
        </div>
      </div>
    );
  }

  // Calculate total payments
  const totalPayments = scope.payments.reduce((sum, payment) => sum + payment.amount, 0);
  
  // Calculate remaining budget
  const remainingBudget = scope.budget ? scope.budget - totalPayments : null;

  return (
    <div className="py-4">
      <button
        onClick={onBack}
        className="inline-flex items-center mb-3 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowUturnLeftIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
        Back
      </button>
      
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">
            <EditableField 
              value={scope.scope} 
              fieldName="scope"
              shortName={scope.short_name}
              placeholder="Enter scope name"
              onChangeSuccess={refreshScopeData}
              className="text-xl font-semibold"
            />
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Construction scope detail information.
          </p>
        </div>
      </div>
      
      <div className="mt-6 border-t border-gray-100">
        <dl className="divide-y divide-gray-100">
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Status</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <StatusSelector 
                selectedStatus={selectedStatus}
                handleStatusChange={handleStatusChange}
                updatingStatus={updatingStatus}
                statusError={statusError}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Subcontractor</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <EditableField 
                value={scope.subcontractor} 
                fieldName="subcontractor"
                shortName={scope.short_name}
                placeholder="Enter subcontractor name"
                onChangeSuccess={refreshScopeData}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Point of Contact</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <EditableField 
                value={scope.point_of_contact} 
                fieldName="point_of_contact"
                shortName={scope.short_name}
                placeholder="Enter point of contact"
                onChangeSuccess={refreshScopeData}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Phone</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <EditableField 
                value={scope.phone} 
                fieldName="phone"
                shortName={scope.short_name}
                placeholder="Enter phone number"
                onChangeSuccess={refreshScopeData}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Budget</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <EditableField 
                value={scope.budget} 
                fieldName="budget"
                shortName={scope.short_name}
                placeholder="Enter budget amount"
                isNumeric={true}
                formatDisplay={formatCurrency}
                onChangeSuccess={refreshScopeData}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Timeline</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="start_month" className="block text-xs text-gray-500 mb-1">Start Month</label>
                  <MonthSelector 
                    value={scope.timeline_start || 0}
                    monthName={scope.start_month_name}
                    fieldName="timeline_start"
                    shortName={scope.short_name}
                    months={Array.isArray(scope.all_months) ? scope.all_months : []}
                    // No constraints for start month - allow all months
                    maxIndex={null}
                    minIndex={null}
                    onChangeSuccess={refreshScopeData}
                  />
                </div>
                <div>
                  <label htmlFor="end_month" className="block text-xs text-gray-500 mb-1">End Month</label>
                  <MonthSelector 
                    value={scope.timeline_start + scope.timeline_duration - 1}
                    monthName={scope.end_month_name}
                    fieldName="timeline_end"
                    shortName={scope.short_name}
                    months={Array.isArray(scope.all_months) ? scope.all_months : []}
                    // End month must be at least the start month
                    minIndex={scope.timeline_start}
                    maxIndex={null}
                    onChangeSuccess={refreshScopeData}
                  />
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {scope.start_month_name && scope.end_month_name && (
                  <span>
                    {scope.start_month_name === scope.end_month_name ? 
                      `This scope runs only during ${scope.start_month_name}` :
                      `This scope runs from ${scope.start_month_name} to ${scope.end_month_name}`
                    }
                    <span className="ml-1 text-gray-600">
                      {' '}({scope.timeline_duration || 3} month{scope.timeline_duration !== 1 ? 's' : ''})
                    </span>
                  </span>
                )}
              </div>
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Budget Distribution</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <DistributionSelector
                value={scope.distribution || 'even'}
                shortName={scope.short_name}
                onChangeSuccess={refreshScopeData}
              />
              <div className="mt-2 text-xs text-gray-500">
                This controls how budget is distributed over the timeline duration
              </div>
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Completion</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <CompletionPercentage
                completionPercent={completionPercent}
                updatingCompletion={updatingCompletion}
                completionError={completionError}
                handleCompletionChange={handleCompletionChange}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Financial Summary</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <FinancialSummary 
                budget={scope.budget}
                totalPayments={totalPayments}
                remainingBudget={remainingBudget}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Notes</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
              <MultilineEditableField 
                value={scope.notes} 
                fieldName="notes"
                shortName={scope.short_name}
                placeholder="Enter notes"
                onChangeSuccess={refreshScopeData}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Contracts</dt>
            <dd className="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
              <ContractsList 
                contracts={scope.contracts} 
                shortName={scope.short_name} 
                onRefresh={refreshScopeData}
                subcontractor={scope.subcontractor}
              />
            </dd>
          </div>
          
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium text-gray-900">Payments</dt>
            <dd className="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
              <PaymentsList 
                payments={scope.payments || []} 
                shortName={scope.short_name} 
                onRefresh={refreshScopeData}
              />
            </dd>
          </div>
        </dl>
      </div>

      {/* Activity Feed - Comments and Edit History */}
      <div className="mt-8 border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-900">Activity</h2>
        <ActivityFeed 
          scope={scope} 
          onAddComment={async (content, files = null, refreshOnly = false) => {
            try {
              // If refreshOnly is true, just refresh the data (for edit/delete operations)
              if (refreshOnly) {
                refreshScopeData();
                return;
              }
              
              // Otherwise add the new comment with optional files
              if (content || files) {
                const { addScopeComment } = await import('../../utils/api');
                await addScopeComment(scope.short_name, content || '', files);
                refreshScopeData();
              }
            } catch (error) {
              console.error('Error adding comment:', error);
              throw error;
            }
          }} 
        />
      </div>
    </div>
  );
}